import React, { useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import InputText from "../inputText/inputText"
import styles from "./hotelRates.module.scss"
import { HotelRoomTypeModel, PeakDateSurchargeGroupModel, PeakDateSurchargeRoomTypeGroupModel, PeakDateSurchargeRoomTypeModel } from "../../models"
import Button from "../button/button"
import Checkbox from "../checkbox/checkbox"
import IconsColor from "../../util/iconsColor"
import { toTitleCase } from "util/textcase"

type Props = {
    peakDateSurchargeGroups: PeakDateSurchargeGroupModel[]
    hotelRoomTypes: HotelRoomTypeModel[]
    varyByRoomType: boolean
    // Legacy contract-level shape (kept for backward compatibility).
    // flag_peak_date_surcharge_is_percentage is a single shared unit for the whole section
    // (applies to both standard & different amounts).
    peakDateSurchage?: { standard_surchage: number | null; different_surcharge: number | null; flag_peak_date_surcharge_is_percentage?: boolean }
    onChangePeakDateSurchage?: (next: { standard_surchage: number | null; different_surcharge: number | null; flag_peak_date_surcharge_is_percentage: boolean }) => void
    readOnly?: boolean
    onChangePeakDateSurchargeGroups?: (next: PeakDateSurchargeGroupModel[]) => void
}

type UiRoom = {
    peak_date_surcharge_room_type_id?: string | null
    hotel_room_type_id: string
    hotel_room_type_name: string
}

type SurchargeMode = "VND" | "%"

type UiGroup = {
    uiId: string
    peak_date_surcharge_room_type_group_id?: string | null
    peak_season_surcharge: number
    // true => peak_season_surcharge is a percentage of base price; false => flat VND amount
    is_percentage: boolean
    peak_date_surcharge_room_type: UiRoom[]
    // which PeakDateSurchargeGroupModel this belongs to (by id). Needed to preserve backend linkage.
    parent_peak_date_surcharge_group_id: string | null
}

const makeUiId = () => `ui_${Date.now()}_${Math.random().toString(16).slice(2)}`

const buildRoomDisplayName = (roomTypeName: string, bedTypeName?: string | null) => {
    const trimmedRoom = (roomTypeName ?? "").trim()
    const trimmedBed = (bedTypeName ?? "").trim()
    if (!trimmedBed) return trimmedRoom
    return `${toTitleCase(trimmedRoom)} (${toTitleCase(trimmedBed)})`
}

const toPositiveIntOrNull = (v: string): number | null => {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    if (!Number.isFinite(n) || n < 0) return null
    // peak surcharges are currency; treat as integer
    return Math.floor(n)
}

// Percentages may be fractional (e.g. 12.5%). Round to 2 decimals to avoid float noise
// and to keep equality comparisons (used when merging groups) stable.
const toPositivePercentOrNull = (v: string): number | null => {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    if (!Number.isFinite(n) || n < 0) return null
    return Math.round(n * 100) / 100
}

// Parse the entered value with the right precision rule for the selected mode.
const parseAmountByMode = (v: string, mode: SurchargeMode): number | null =>
    mode === "%" ? toPositivePercentOrNull(v) : toPositiveIntOrNull(v)

// Percentages are bounded 0–100; VND has no upper cap. A negative/non-numeric value is
// already rejected by parseAmountByMode (returns null).
const PERCENT_MAX = 100

// Returns the value only when it's valid for the mode, else null. Used for committing /
// enabling actions so an out-of-range percentage (e.g. 250%) is never persisted.
const sanitizeAmount = (v: string, mode: SurchargeMode): number | null => {
    const n = parseAmountByMode(v, mode)
    if (n === null) return null
    if (mode === "%" && n > PERCENT_MAX) return null
    return n
}

// User-facing range error (null when fine). Only flags the out-of-range case so an empty
// field doesn't show an error while the user is still typing.
const amountRangeError = (v: string, mode: SurchargeMode): string | null => {
    const n = parseAmountByMode(v, mode)
    if (n !== null && mode === "%" && n > PERCENT_MAX) return `Percentage must be between 0 and ${PERCENT_MAX}`
    return null
}

// Two groups are "the same bucket" (and should merge) only when both the value AND the
// unit match — 10 VND and 10% must stay separate.
const sortUiGroups = (arr: UiGroup[]): UiGroup[] =>
    arr.sort(
        (a, b) =>
            Number(a.is_percentage) - Number(b.is_percentage) ||
            a.peak_season_surcharge - b.peak_season_surcharge ||
            b.peak_date_surcharge_room_type.length - a.peak_date_surcharge_room_type.length,
    )

const formatRange = (start: string, end: string) => {
    const s = moment(start)
    const e = moment(end)
    if (!s.isValid() || !e.isValid()) return ""
    return `${s.format("DD MMM YYYY")} to ${e.format("DD MMM YYYY")}`
}

const deriveUiGroups = (peakGroups: PeakDateSurchargeGroupModel[]): UiGroup[] => {
    const out: UiGroup[] = []
    peakGroups.forEach((pg) => {
        const parentId = pg.peak_date_surcharge_group_id ?? null
        ;(pg.peak_date_surcharge_room_type_group || []).forEach((g) => {
            const rooms: UiRoom[] = (g.peak_date_surcharge_room_type || [])
                .filter((rt) => !!rt?.hotel_room_type_id)
                .map((rt) => ({
                    peak_date_surcharge_room_type_id: rt.peak_date_surcharge_room_type_id ?? null,
                    hotel_room_type_id: rt.hotel_room_type_id,
                    hotel_room_type_name: rt.hotel_room_type_name ?? "",
                }))

            // Skip empty groups
            const amount = typeof g.peak_season_surcharge === "number" ? g.peak_season_surcharge : Number(g.peak_season_surcharge)
            if (!Number.isFinite(amount) || rooms.length === 0) return

            out.push({
                uiId: makeUiId(),
                peak_date_surcharge_room_type_group_id: g.peak_date_surcharge_room_type_group_id ?? null,
                peak_season_surcharge: amount,
                is_percentage: g.flag_peak_date_surcharge_is_percentage === true,
                peak_date_surcharge_room_type: rooms,
                parent_peak_date_surcharge_group_id: parentId,
            })
        })
    })

    // Sort: VND groups first, then amount asc, then room count desc (stable-ish)
    return sortUiGroups(out)
}

/** Compact VND/% unit dropdown. Used inside the Quick Assign field (overlaid on the
 *  right) and inline in the per-group Edit Amount editor. */
const ModeToggle: React.FC<{
    value: SurchargeMode
    onChange: (mode: SurchargeMode) => void
    disabled?: boolean
}> = ({ value, onChange, disabled }) => (
    <div className={styles.modeSelectWrapper}>
        <select
            className={styles.modeSelect}
            value={value}
            onChange={(e) => onChange(e.target.value as SurchargeMode)}
            disabled={disabled}
            aria-label="Surcharge unit"
        >
            <option value="VND">VND</option>
            <option value="%">%</option>
        </select>
        <span className={styles.modeSelectChevron}>
            <IconsColor iconName="chevronDown" width="14px" height="14px" fillColor="none" strokeColor="currentColor" />
        </span>
    </div>
)

const RoomSpecificPeakDateSurcharges: React.FC<Props> = ({
    peakDateSurchargeGroups,
    hotelRoomTypes,
    varyByRoomType,
    peakDateSurchage,
    onChangePeakDateSurchage,
    readOnly = false,
    onChangePeakDateSurchargeGroups,
}) => {
    const roomMetaById = useMemo(() => {
        const map = new Map<string, { name: string; bedTypeName: string | null }>()
        hotelRoomTypes.forEach((rt) => {
            const id = rt.hotel_room_type_id
            const name = rt.hotel_room_type_name
            if (!id || !name) return
            map.set(id, { name, bedTypeName: rt.bed_type_name ?? null })
        })
        return map
    }, [hotelRoomTypes])

    const getRoomLabel = (roomTypeId: string, fallbackName?: string) => {
        const meta = roomMetaById.get(roomTypeId)
        const name = meta?.name ?? (fallbackName ?? "")
        return buildRoomDisplayName(name, meta?.bedTypeName ?? null)
    }

    const defaultParentPeakGroupId = useMemo(() => {
        const standard = peakDateSurchargeGroups.find((g) => g.flag_different_surcharge === false && g.peak_date_surcharge_group_id)
        return (standard?.peak_date_surcharge_group_id ?? peakDateSurchargeGroups[0]?.peak_date_surcharge_group_id ?? null) as string | null
    }, [peakDateSurchargeGroups])

    // const allPeakRangesLabel = useMemo(() => {
    //     const ranges =
    //         peakDateSurchargeGroups.flatMap((g) => g.peak_date_range || []).map((r) => ({
    //             start: r.start_date,
    //             end: r.end_date,
    //         })) || []

    //     const sorted = ranges
    //         .filter((r) => !!r.start && !!r.end)
    //         .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

    //     return sorted.map((r) => formatRange(r.start, r.end)).filter(Boolean).join(" • ")
    // }, [peakDateSurchargeGroups])

    const peakRangeItems = useMemo(() => {
        const items: { label: string; isDifferent: boolean; startTs: number }[] = []
        peakDateSurchargeGroups.forEach((g) => {
            ;(g.peak_date_range || []).forEach((r) => {
                if (!r.start_date || !r.end_date) return
                const startTs = new Date(r.start_date).getTime()
                items.push({
                    label: formatRange(r.start_date, r.end_date),
                    isDifferent: g.flag_different_surcharge === true,
                    startTs: Number.isFinite(startTs) ? startTs : Number.MAX_SAFE_INTEGER,
                })
            })
        })
        return items
            .filter((x) => !!x.label)
            .sort((x, y) => x.startTs - y.startTs)
            .map(({ label, isDifferent }) => ({ label, isDifferent }))
    }, [peakDateSurchargeGroups])

    const hasDifferentSurchargeGroup = useMemo(() => {
        return (peakDateSurchargeGroups || []).some((g) => g.flag_different_surcharge === true)
    }, [peakDateSurchargeGroups])

    const [uiGroups, setUiGroups] = useState<UiGroup[]>(() => deriveUiGroups(peakDateSurchargeGroups))
    const peakGroupsKey = useMemo(() => JSON.stringify(peakDateSurchargeGroups ?? []), [peakDateSurchargeGroups])
    const lastSyncedKey = useRef<string>("")

    useEffect(() => {
        if (peakGroupsKey === lastSyncedKey.current) return
        lastSyncedKey.current = peakGroupsKey
        setUiGroups(deriveUiGroups(peakDateSurchargeGroups))
    }, [peakGroupsKey, peakDateSurchargeGroups])

    const allRoomOptions = useMemo(() => {
        return hotelRoomTypes
            .filter((rt) => rt.hotel_room_type_id && rt.hotel_room_type_name)
            .map((rt) => ({
                id: rt.hotel_room_type_id as string,
                name: (rt.hotel_room_type_name as string) || "",
                bedTypeName: rt.bed_type_name ?? null,
            }))
    }, [hotelRoomTypes])

    const assignedRoomTypeIds = useMemo(() => {
        const s = new Set<string>()
        uiGroups.forEach((g) => g.peak_date_surcharge_room_type.forEach((rt) => s.add(rt.hotel_room_type_id)))
        return s
    }, [uiGroups])

    const unassignedRooms = useMemo(() => {
        return allRoomOptions.filter((r) => !assignedRoomTypeIds.has(r.id))
    }, [allRoomOptions, assignedRoomTypeIds])

    const [quickAmount, setQuickAmount] = useState<string>("")
    const [quickMode, setQuickMode] = useState<SurchargeMode>("VND")
    const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set())
    const quickAmountValue = useMemo(() => sanitizeAmount(quickAmount, quickMode), [quickAmount, quickMode])
    const quickAmountError = useMemo(() => amountRangeError(quickAmount, quickMode), [quickAmount, quickMode])

    const setRoomSelected = (roomTypeId: string, selected: boolean) => {
        setSelectedRoomIds((prev) => {
            const next = new Set(prev)
            if (selected) next.add(roomTypeId)
            else next.delete(roomTypeId)
            return next
        })
    }

    const persistToParent = (nextUiGroups: UiGroup[]) => {
        if (!onChangePeakDateSurchargeGroups) return
        const peakGroups = (peakDateSurchargeGroups || []).map((pg) => ({ ...pg }))

        // Build groups per parent peak group id
        const byParent: Record<string, PeakDateSurchargeRoomTypeGroupModel[]> = {}
        nextUiGroups.forEach((g) => {
            const parentId = g.parent_peak_date_surcharge_group_id ?? defaultParentPeakGroupId
            if (!parentId) return
            if (!byParent[parentId]) byParent[parentId] = []

            const roomTypes: PeakDateSurchargeRoomTypeModel[] = g.peak_date_surcharge_room_type.map((rt) => ({
                peak_date_surcharge_room_type_id: rt.peak_date_surcharge_room_type_id ?? null,
                hotel_room_type_id: rt.hotel_room_type_id,
                hotel_room_type_name: rt.hotel_room_type_name,
            }))

            byParent[parentId].push({
                peak_date_surcharge_room_type_group_id: g.peak_date_surcharge_room_type_group_id ?? null,
                peak_season_surcharge: g.peak_season_surcharge,
                flag_peak_date_surcharge_is_percentage: g.is_percentage,
                peak_date_surcharge_room_type: roomTypes,
            })
        })

        // Apply to peak groups (preserve peak date ranges & other fields)
        const nextPeakGroups: PeakDateSurchargeGroupModel[] = peakGroups.map((pg) => {
            const id = pg.peak_date_surcharge_group_id ?? ""
            const nextGroups = id ? byParent[id] : undefined
            return {
                ...pg,
                peak_date_surcharge_room_type_group: nextGroups && nextGroups.length > 0 ? nextGroups : [],
            }
        })

        onChangePeakDateSurchargeGroups(nextPeakGroups)
    }

    const commitUiGroups = (next: UiGroup[]) => {
        setUiGroups(next)
        persistToParent(next)
    }

    const applyQuickAssign = () => {
        if (readOnly) return
        if (!defaultParentPeakGroupId) return
        const amount = quickAmountValue
        if (amount === null) return
        if (selectedRoomIds.size === 0) return
        const isPercentage = quickMode === "%"

        const selected = new Set(selectedRoomIds)
        const selectedRooms: UiRoom[] = unassignedRooms
            .filter((r) => selected.has(r.id))
            .map((r) => ({
                peak_date_surcharge_room_type_id: null,
                hotel_room_type_id: r.id,
                hotel_room_type_name: r.name,
            }))

        // Remove selected rooms from any existing group (safety)
        let next = uiGroups
            .map((g) => ({
                ...g,
                peak_date_surcharge_room_type: g.peak_date_surcharge_room_type.filter((rt) => !selected.has(rt.hotel_room_type_id)),
            }))
            .filter((g) => g.peak_date_surcharge_room_type.length > 0)

        // Find group with the same amount AND unit (within same parent). A 10 VND group and a
        // 10% group are different buckets and must not merge.
        const idx = next.findIndex(
            (g) =>
                g.peak_season_surcharge === amount &&
                g.is_percentage === isPercentage &&
                (g.parent_peak_date_surcharge_group_id ?? defaultParentPeakGroupId) === defaultParentPeakGroupId
        )

        if (idx >= 0) {
            const existing = next[idx]
            const existingIds = new Set(existing.peak_date_surcharge_room_type.map((rt) => rt.hotel_room_type_id))
            const mergedRooms = [...existing.peak_date_surcharge_room_type, ...selectedRooms.filter((rt) => !existingIds.has(rt.hotel_room_type_id))]
            next = next.map((g, i) => (i === idx ? { ...g, peak_date_surcharge_room_type: mergedRooms } : g))
        } else {
            next = [
                ...next,
                {
                    uiId: makeUiId(),
                    peak_date_surcharge_room_type_group_id: null,
                    peak_season_surcharge: amount,
                    is_percentage: isPercentage,
                    peak_date_surcharge_room_type: selectedRooms,
                    parent_peak_date_surcharge_group_id: defaultParentPeakGroupId,
                },
            ]
        }

        // Re-sort
        sortUiGroups(next)
        commitUiGroups(next)
        setQuickAmount("")
        setSelectedRoomIds(new Set())
    }

    const removeGroup = (uiId: string) => {
        if (readOnly) return
        commitUiGroups(uiGroups.filter((g) => g.uiId !== uiId))
    }

    const [editingAmountFor, setEditingAmountFor] = useState<string | null>(null)
    const [editAmount, setEditAmount] = useState<string>("")
    const [editMode, setEditMode] = useState<SurchargeMode>("VND")

    const startEditAmount = (g: UiGroup) => {
        if (readOnly) return
        setEditingAmountFor(g.uiId)
        setEditAmount(String(g.peak_season_surcharge ?? ""))
        setEditMode(g.is_percentage ? "%" : "VND")
    }

    const saveEditAmount = (g: UiGroup) => {
        if (readOnly) return
        const nextAmount = sanitizeAmount(editAmount, editMode)
        if (nextAmount === null) return
        const nextIsPercentage = editMode === "%"

        // If another group already uses this amount AND unit, merge into it
        const existingIdx = uiGroups.findIndex(
            (x) => x.uiId !== g.uiId && x.peak_season_surcharge === nextAmount && x.is_percentage === nextIsPercentage,
        )
        if (existingIdx >= 0) {
            const target = uiGroups[existingIdx]
            const targetIds = new Set(target.peak_date_surcharge_room_type.map((rt) => rt.hotel_room_type_id))
            const mergedRooms = [
                ...target.peak_date_surcharge_room_type,
                ...g.peak_date_surcharge_room_type.filter((rt) => !targetIds.has(rt.hotel_room_type_id)),
            ]

            const next = sortUiGroups(
                uiGroups
                    .filter((x) => x.uiId !== g.uiId)
                    .map((x) => (x.uiId === target.uiId ? { ...x, peak_date_surcharge_room_type: mergedRooms } : x)),
            )
            commitUiGroups(next)
        } else {
            const next = sortUiGroups(
                uiGroups.map((x) =>
                    x.uiId === g.uiId ? { ...x, peak_season_surcharge: nextAmount, is_percentage: nextIsPercentage } : x,
                ),
            )
            commitUiGroups(next)
        }

        setEditingAmountFor(null)
        setEditAmount("")
        setEditMode("VND")
    }

    const canQuickAssign = !!defaultParentPeakGroupId && unassignedRooms.length > 0
    const quickAssignDisabled = readOnly || !canQuickAssign || quickAmountValue === null || selectedRoomIds.size === 0

    // Contract-level peak surcharges (when not varying by room type)
    const [standardSurchageStr, setStandardSurchageStr] = useState<string>("")
    const [differentSurchargeStr, setDifferentSurchargeStr] = useState<string>("")
    // Single shared VND/% unit for this whole section (both standard & different).
    const [contractMode, setContractMode] = useState<SurchargeMode>("VND")

    // Hydrate the shared unit from the saved flag on the legacy peak_date_surchage object.
    useEffect(() => {
        if (varyByRoomType) return
        setContractMode(peakDateSurchage?.flag_peak_date_surcharge_is_percentage ? "%" : "VND")
    }, [varyByRoomType, peakDateSurchage?.flag_peak_date_surcharge_is_percentage])

    const standardPeakSeasonSurchargeFromGroups = useMemo(() => {
        const g = (peakDateSurchargeGroups || []).find((x) => x.flag_different_surcharge === false)
        return g?.standard_peak_season_surcharge ?? null
    }, [peakDateSurchargeGroups])

    const differentPeakSeasonSurchargeFromGroups = useMemo(() => {
        const g = (peakDateSurchargeGroups || []).find((x) => x.flag_different_surcharge === true)
        return g?.different_peak_season_surcharge ?? null
    }, [peakDateSurchargeGroups])

    useEffect(() => {
        if (varyByRoomType) return

        // Prefer reading from peak_date_surcharge_group (new API shape),
        // and fall back to legacy peak_date_surchage if needed.
        const standardValue =
            standardPeakSeasonSurchargeFromGroups !== null && standardPeakSeasonSurchargeFromGroups !== undefined
                ? standardPeakSeasonSurchargeFromGroups
                : peakDateSurchage?.standard_surchage ?? null

        setStandardSurchageStr(
            standardValue === null || standardValue === undefined ? "" : String(standardValue)
        )

        setDifferentSurchargeStr(() => {
            if (!hasDifferentSurchargeGroup) return ""

            const differentValue =
                differentPeakSeasonSurchargeFromGroups !== null && differentPeakSeasonSurchargeFromGroups !== undefined
                    ? differentPeakSeasonSurchargeFromGroups
                    : peakDateSurchage?.different_surcharge ?? null

            return differentValue === null || differentValue === undefined ? "" : String(differentValue)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        varyByRoomType,
        standardPeakSeasonSurchargeFromGroups,
        differentPeakSeasonSurchargeFromGroups,
        peakDateSurchage?.standard_surchage,
        peakDateSurchage?.different_surcharge,
        hasDifferentSurchargeGroup,
    ])

    const commitContractLevelPeakSurcharges = (next: { standard: number | null; different: number | null; isPercentage: boolean }) => {
        // Primary write: update peak_date_surcharge_group values based on flag_different_surcharge
        if (onChangePeakDateSurchargeGroups) {
            const nextGroups: PeakDateSurchargeGroupModel[] = (peakDateSurchargeGroups || []).map((g) => {
                if (g.flag_different_surcharge === false) {
                    return { ...g, standard_peak_season_surcharge: next.standard }
                }
                if (g.flag_different_surcharge === true) {
                    return { ...g, different_peak_season_surcharge: next.different }
                }
                return g
            })
            onChangePeakDateSurchargeGroups(nextGroups)
        }

        // Secondary write: keep legacy peak_date_surchage in sync (if consumer still relies on it).
        // The shared VND/% unit is saved here as flag_peak_date_surcharge_is_percentage.
        if (onChangePeakDateSurchage) {
            onChangePeakDateSurchage({
                standard_surchage: next.standard,
                different_surcharge: hasDifferentSurchargeGroup ? next.different : null,
                flag_peak_date_surcharge_is_percentage: next.isPercentage,
            })
        }
    }

    // Switching the shared unit re-commits the current amounts with the new flag. Values that
    // are now out of range for the new unit (e.g. 250 after switching to %) commit as null and
    // surface an inline error (see the error props below) until the user fixes them.
    const handleContractModeChange = (mode: SurchargeMode) => {
        if (readOnly) return
        setContractMode(mode)
        commitContractLevelPeakSurcharges({
            standard: sanitizeAmount(standardSurchageStr, mode),
            different: hasDifferentSurchargeGroup ? sanitizeAmount(differentSurchargeStr, mode) : null,
            isPercentage: mode === "%",
        })
    }

    if (!varyByRoomType) {
        return (
            <div className={styles.peakRoomSurchargeCard}>
                <div className={styles.peakRoomSurchargeHeader}>
                    <div>
                        <div className={styles.peakRoomSurchargeTitle}>Peak Date Surcharges</div>
                    </div>
                </div>

                <div className={styles.peakRoomSurchargeBody}>
                    <div className={styles.appliedPeakDatesRow}>
                        <div className={styles.appliedPeakDatesLabel}>Applied To Peak Dates:</div>
                        <div className={styles.appliedPeakDatesValue}>
                            {peakRangeItems.length === 0 ? (
                                <span className={styles.appliedPeakDatesEmpty}>No Peak Dates Configured</span>
                            ) : (
                                <ul className={styles.peakDateList}>
                                    {peakRangeItems.map((it, idx) => (
                                        <li key={`${it.label}_${idx}`} className={styles.peakDateListItem}>
                                            <span>{toTitleCase(it.label)}</span>
                                            {it.isDifferent && <span className={styles.differentSurchargeBadge}>Different Surcharge</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className={styles.peakDateSurchargeGrid}>
                        <div>
                            <div className={styles.peakDateSurchargeLabel}>Standard Peak Season Surcharge ({contractMode === "%" ? "%" : "VND"}/Room/Night)</div>
                            <div className={styles.surchargeFieldWrapper}>
                                <InputText
                                    label="Standard Peak Season Surcharge"
                                    hideLabel={true}
                                    noMargin={true}
                                    placeholder={contractMode === "%" ? "Enter percentage" : "Enter amount"}
                                    value={standardSurchageStr}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        setStandardSurchageStr(v)
                                        commitContractLevelPeakSurcharges({
                                            standard: sanitizeAmount(v, contractMode),
                                            different: hasDifferentSurchargeGroup ? sanitizeAmount(differentSurchargeStr, contractMode) : null,
                                            isPercentage: contractMode === "%",
                                        })
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                    }}
                                    type="number"
                                    disabled={readOnly}
                                    sx={{ paddingRight: "92px" }}
                                    error={amountRangeError(standardSurchageStr, contractMode) ?? undefined}
                                />
                                <div className={styles.modeToggleOverlay}>
                                    <ModeToggle value={contractMode} onChange={handleContractModeChange} disabled={readOnly} />
                                </div>
                            </div>
                        </div>
                        {hasDifferentSurchargeGroup ? (
                            <div>
                                <div className={styles.peakDateSurchargeLabel}>Different Peak Season Surcharge ({contractMode === "%" ? "%" : "VND"}/Room/Night)</div>
                                <div className={styles.surchargeFieldWrapper}>
                                    <InputText
                                        label="Different Peak Season Surcharge"
                                        hideLabel={true}
                                        noMargin={true}
                                        placeholder={contractMode === "%" ? "Enter percentage" : "Enter amount"}
                                        value={differentSurchargeStr}
                                        onChange={(e) => {
                                            const v = e.target.value
                                            setDifferentSurchargeStr(v)
                                            commitContractLevelPeakSurcharges({
                                                standard: sanitizeAmount(standardSurchageStr, contractMode),
                                                different: sanitizeAmount(v, contractMode),
                                                isPercentage: contractMode === "%",
                                            })
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                        }}
                                        type="number"
                                        disabled={readOnly}
                                        sx={{ paddingRight: "92px" }}
                                        error={amountRangeError(differentSurchargeStr, contractMode) ?? undefined}
                                    />
                                    <div className={styles.modeToggleOverlay}>
                                        <ModeToggle value={contractMode} onChange={handleContractModeChange} disabled={readOnly} />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.peakRoomSurchargeCard}>
            <div className={styles.peakRoomSurchargeHeader}>
                <div>
                    <div className={styles.peakRoomSurchargeTitle}>Room-Specific Peak Date Surcharges</div>
                    {/* <div className={styles.peakRoomSurchargeSubTitle}>Group rooms by surcharge amount for easier management</div> */}
                </div>
            </div>

            <div className={styles.peakRoomSurchargeBody}>
                <div className={styles.appliedPeakDatesRow}>
                    {/* <div className={styles.appliedPeakDatesLabel}>Applied to Peak Dates:</div> */}
                    {/* <div className={styles.appliedPeakDatesValue}>
                        {allPeakRangesLabel || <span className={styles.appliedPeakDatesEmpty}>No peak dates configured</span>}
                    </div> */}
                    <div className={styles.appliedPeakDatesRow}>
                        <div className={styles.appliedPeakDatesLabel}>Applied To Peak Dates:</div>
                        <div className={styles.appliedPeakDatesValue}>
                            {peakRangeItems.length === 0 ? (
                                <span className={styles.appliedPeakDatesEmpty}>No Peak Dates Configured</span>
                            ) : (
                                <ul className={styles.peakDateList}>
                                    {peakRangeItems.map((it, idx) => (
                                        <li key={`${it.label}_${idx}`} className={styles.peakDateListItem}>
                                            <span>{toTitleCase(it.label)}</span>
                                            {it.isDifferent && <span className={styles.differentSurchargeBadge}>Different Surcharge</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.quickAssignBox}>
                    <div className={styles.quickAssignTitle}>Quick Assign</div>
                    <div className={styles.quickAssignControls}>
                        <div className={styles.quickAssignInput}>
                            <InputText
                                label={quickMode === "%" ? "Enter percentage (%)" : "Enter amount (VND)"}
                                hideLabel={true}
                                noMargin={true}
                                placeholder={quickMode === "%" ? "Enter percentage (%)" : "Enter amount (VND)"}
                                value={quickAmount}
                                onChange={(e) => setQuickAmount(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                }}
                                type="number"
                                disabled={readOnly || !canQuickAssign}
                                sx={{ paddingRight: "92px" }}
                                error={quickAmountError ?? undefined}
                            />
                            <div className={styles.modeToggleOverlay}>
                                <ModeToggle
                                    value={quickMode}
                                    onChange={setQuickMode}
                                    disabled={readOnly || !canQuickAssign}
                                />
                            </div>
                        </div>
                        <div title={!defaultParentPeakGroupId ? "Please configure Peak date ranges first" : undefined}>
                            <Button
                                type="contained"
                                label="Apply to Selected Rooms"
                                onClick={applyQuickAssign}
                                disabled={quickAssignDisabled}
                                style={{ minWidth: "unset", padding: "10px 14px", whiteSpace: "nowrap" }}
                            />
                        </div>
                    </div>

                    <div className={styles.roomChipsRow}>
                        {(unassignedRooms.length > 0 ? unassignedRooms : []).map((r) => {
                            const checked = selectedRoomIds.has(r.id)
                            return (
                                <div
                                    key={r.id}
                                    className={`${styles.roomChip} ${checked ? styles.roomChipSelected : ""} ${readOnly ? styles.roomChipDisabled : ""}`}
                                >
                                    <Checkbox
                                        label={buildRoomDisplayName(r.name, r.bedTypeName)}
                                        checked={checked}
                                        onChange={(next) => setRoomSelected(r.id, next)}
                                        disabled={readOnly}
                                        size="small"
                                    />
                                </div>
                            )
                        })}
                        {unassignedRooms.length === 0 && (
                            <div className={styles.noUnassignedRooms}>All Rooms Have Been Assigned To A Surcharge Group.</div>
                        )}
                    </div>
                </div>

                {uiGroups.map((g, idx) => (
                    <div key={g.uiId} className={styles.surchargeGroupCard}>
                        <div className={styles.surchargeGroupHeader}>
                            <div className={styles.surchargeGroupTitle}>
                                Surcharge Group {idx + 1}&nbsp;&nbsp;
                                {editingAmountFor === g.uiId ? (
                                    <span className={styles.surchargeGroupAmountEdit}>
                                        <input
                                            className={styles.surchargeGroupAmountInput}
                                            value={editAmount}
                                            onChange={(e) => setEditAmount(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                                if (e.key === "Enter") saveEditAmount(g)
                                                if (e.key === "Escape") {
                                                    setEditingAmountFor(null)
                                                    setEditAmount("")
                                                }
                                            }}
                                            type="number"
                                            disabled={readOnly}
                                        />
                                        <ModeToggle value={editMode} onChange={setEditMode} disabled={readOnly} />
                                        <Button
                                            type="contained"
                                            label="Save"
                                            onClick={() => saveEditAmount(g)}
                                            disabled={readOnly || sanitizeAmount(editAmount, editMode) === null}
                                            style={{ minWidth: "unset", padding: "6px 10px", fontSize: 12 }}
                                        />
                                        <Button
                                            type="outlined"
                                            label="Cancel"
                                            onClick={() => {
                                                setEditingAmountFor(null)
                                                setEditAmount("")
                                            }}
                                            disabled={readOnly}
                                            style={{ minWidth: "unset", padding: "6px 10px", fontSize: 12 }}
                                        />
                                        {amountRangeError(editAmount, editMode) && (
                                            <span className={styles.surchargeAmountError}>{amountRangeError(editAmount, editMode)}</span>
                                        )}
                                    </span>
                                ) : (
                                    <span className={styles.surchargeGroupAmount}>{String(g.peak_season_surcharge)} {g.is_percentage ? "%" : "VND"}</span>
                                )}
                            </div>
                            <div className={styles.surchargeGroupActions}>
                                {editingAmountFor !== g.uiId && (
                                    <Button
                                        type="contained"
                                        label="Edit Amount"
                                        onClick={() => startEditAmount(g)}
                                        disabled={readOnly}
                                        style={{ minWidth: "unset", padding: "7px 12px", fontSize: 12 }}
                                    />
                                )}
                                <Button
                                    type="contained"
                                    label="Remove Group"
                                    onClick={() => removeGroup(g.uiId)}
                                    disabled={readOnly}
                                    // matches theme danger color from Color.scss ($danger = #dc3545)
                                    style={{
                                        minWidth: "unset",
                                        padding: "7px 12px",
                                        fontSize: 12,
                                        backgroundColor: "#dc3545",
                                        borderColor: "#dc3545",
                                        color: "#ffffff",
                                    }}
                                />
                            </div>
                        </div>

                        <div className={styles.surchargeGroupBody}>
                            <div className={styles.roomItemGrid}>
                                {g.peak_date_surcharge_room_type.map((rt) => (
                                    <div key={rt.hotel_room_type_id} className={styles.roomItem}>
                                        <span className={styles.greenDot} />
                                        <span className={styles.roomItemText}>{getRoomLabel(rt.hotel_room_type_id, rt.hotel_room_type_name)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}

                {/* <div className={styles.peakRoomSurchargeHelp}>
                    <b>How it works:</b> Rooms are automatically grouped by their surcharge amount. You can edit a group's amount to update all rooms in that group at once, or remove a group to clear surcharges for all rooms in it. Use Quick Assign to quickly create groups from unassigned rooms.
                </div> */}
            </div>
        </div>
    )
}

export default RoomSpecificPeakDateSurcharges
